import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const { loginWithPassword, register, isInternal } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await loginWithPassword(email, password);
        const internal = u?.roles?.some((r) => ["admin", "intern_delivery", "intern_architekt", "intern_audit"].includes(r));
        nav(internal ? "/dashboard" : "/portal");
      } else {
        const u = await register({ email, password, name });
        nav(u?.roles?.includes("admin") ? "/dashboard" : "/portal");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid md:grid-cols-2" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="hidden md:flex flex-col justify-between p-12 border-r border-neutral-200" style={{ background: "#F5F5F4" }}>
        <div>
          <div className="font-display text-3xl" style={{ color: "#183153" }}>AppWerkstatt</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-2">Portal · Audit-fähig · Single Source of Truth</div>
        </div>
        <div className="space-y-6">
          <h1 className="font-display text-4xl text-neutral-900 leading-tight">
            Von Problemdefinition<br />zur produktiven App.
          </h1>
          <p className="text-neutral-700 leading-relaxed max-w-md">
            Strukturiert. Nachvollziehbar. Auditierbar. Discovery, Prompting, Builds, Review, Audit, Sign-off und Betrieb –
            in einer Akte.
          </p>
          <div className="grid grid-cols-7 gap-px bg-neutral-200 border border-neutral-200">
            {["Discovery", "Prompting", "Build", "Review", "Audit", "Sign-off", "Betrieb"].map((p, i) => (
              <div key={p} className="bg-white px-2 py-3 text-[10px] font-mono uppercase tracking-tight text-neutral-700 text-center">
                <div className="text-neutral-400 mb-1">{String(i + 1).padStart(2, "0")}</div>
                {p}
              </div>
            ))}
          </div>
        </div>
        <div className="font-mono text-[10px] text-neutral-500">v 1.0 · ISO-konform · DSGVO</div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="max-w-sm w-full mx-auto">
          <h2 className="font-display text-2xl text-neutral-900 mb-1">
            {mode === "login" ? "Anmelden" : "Konto erstellen"}
          </h2>
          <p className="text-sm text-neutral-600 mb-8">
            {mode === "login"
              ? "Greifen Sie auf Projektakte und Lieferobjekte zu."
              : "Beantragen Sie Zugriff auf das Portal."}
          </p>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
            {mode === "register" && (
              <input
                type="text"
                required
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="login-name-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#183153] text-sm"
              />
            )}
            <input
              type="email"
              required
              placeholder="E-Mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full px-3 py-2 border border-neutral-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#183153] text-sm"
            />
            <input
              type="password"
              required
              minLength={mode === "register" ? 8 : undefined}
              placeholder={mode === "register" ? "Passwort (min 8 Zeichen)" : "Passwort"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full px-3 py-2 border border-neutral-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#183153] text-sm"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full px-4 py-2.5 rounded-sm text-white text-sm font-medium disabled:opacity-50 hover:opacity-95"
              style={{ background: "#183153" }}
            >
              {busy ? "…" : mode === "login" ? "Anmelden" : "Registrieren"}
            </button>
          </form>

          <div className="mt-5 text-xs text-neutral-600">
            {mode === "login" ? (
              <button onClick={() => setMode("register")} className="underline" data-testid="switch-to-register">Konto erstellen</button>
            ) : (
              <button onClick={() => setMode("login")} className="underline" data-testid="switch-to-login">Bereits Konto? Anmelden</button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
